import * as sdk from 'matrix-js-sdk';
import { ClientEvent, MatrixClient, SyncState } from 'matrix-js-sdk';
import { MatrixSessionDto } from 'services/client-api-wrapper/identity_provider/responses.dto';
import type { UIAuthCallback } from 'matrix-js-sdk/lib/interactive-auth';
import type { CryptoApi, CryptoCallbacks, GeneratedSecretStorageKey } from 'matrix-js-sdk/lib/crypto-api';
import { idpService } from 'services/api/identity-provider.service';

// Core (and everything inside it, including MatrixProvider) remounts on every in-app
// navigation because each protected route wraps its own <Core> instead of sharing one
// layout instance. Caching the client at module scope means remounts reuse the existing
// synced client instead of re-fetching a session and reconnecting on every navigation.
let cachedClient: MatrixClient | null = null;
let cachedClientPromise: Promise<MatrixClient> | null = null;

const LAST_MATRIX_DEVICE_ID_KEY = 'lastMatrixDeviceId';

// Identifies this browser origin to the backend so it can hand out a permanent, origin-scoped
// device slot instead of one slot shared (and fought over) by every origin the account is used
// from. Generated once and persisted in localStorage, which is already scoped per origin - that's
// the whole mechanism, no server round-trip needed to obtain it.
const CLIENT_INSTANCE_ID_KEY = 'matrixClientInstanceId';

// Backend enforces this same allowlist server-side and rejects anything else with a 400. If a
// corrupted/tampered value ever ends up in localStorage (DevTools, a browser extension, anything),
// treating it as invalid here (same as "no value") lets the client self-heal by generating a fresh
// one, instead of re-sending the same bad value and 400ing forever.
const CLIENT_INSTANCE_ID_PATTERN = /^[A-Za-z0-9_-]{8,64}$/;

export const getOrCreateClientInstanceId = (): string => {
    const existing = localStorage.getItem(CLIENT_INSTANCE_ID_KEY);
    if (existing && CLIENT_INSTANCE_ID_PATTERN.test(existing)) return existing;

    const generated = crypto.randomUUID();
    localStorage.setItem(CLIENT_INSTANCE_ID_KEY, generated);
    return generated;
};

// Read-only variant for callers (like disconnect) that must never mint a new id - creating one
// there wouldn't correspond to any real device, since a disconnect only makes sense for an id
// that was already persisted by a prior connect.
export const getClientInstanceId = (): string | null => localStorage.getItem(CLIENT_INSTANCE_ID_KEY);

// The app's various sign-out/forced-reconnect paths call a raw localStorage.clear() with no
// awareness of the Matrix connection. Since neither of these keys is a credential (the actual
// access token lives only server-side, keyed by the instance id) and the E2EE crypto store in
// IndexedDB is untouched by those flows, wiping them would strand this origin's live Matrix
// device forever - the next connect would mint a *new* device under a *new* random id, with no
// way to ever reference (and therefore clean up) the old one. Preserving both across the wipe
// means the next login after sign-out reconnects to the exact same device with zero Synapse
// calls and zero E2EE history loss, matching how logout already behaved before this device model
// existed.
export const preserveMatrixIdentityAcrossClear = (clearFn: () => void): void => {
    const clientInstanceId = localStorage.getItem(CLIENT_INSTANCE_ID_KEY);
    const lastDeviceId = localStorage.getItem(LAST_MATRIX_DEVICE_ID_KEY);

    clearFn();

    if (clientInstanceId) localStorage.setItem(CLIENT_INSTANCE_ID_KEY, clientInstanceId);
    if (lastDeviceId) localStorage.setItem(LAST_MATRIX_DEVICE_ID_KEY, lastDeviceId);
};

// Shared across the getSecretStorageKey/cacheSecretStorageKey crypto callbacks (used whenever the
// SDK needs to read an existing secret) and createSecretStorageKey (used only the first time, to
// create one) - all three must agree on the same key for a client's lifetime, and caching it after
// the first fetch avoids redundant backend round-trips on every subsequent secret access.
let cachedSecretStorageKey: Uint8Array | null = null;

// The backend generates this as a plain SecureRandom.hex(32) (see identity-provider's
// MatrixService#generate_secret_storage_key!) - not matrix-js-sdk's base58 "recovery key"
// envelope, so we decode the hex directly rather than going through decodeRecoveryKey. Exported
// so the "reveal sign-in details" panel can convert the same value to encodeRecoveryKey's format
// when it does need the human-facing envelope (for pasting into another client's recovery prompt).
export const hexToBytes = (hex: string): Uint8Array => {
    const bytes = new Uint8Array(hex.length / 2);
    for (let i = 0; i < bytes.length; i++) {
        bytes[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
    }
    return bytes;
};

const fetchSecretStorageKey = async (): Promise<Uint8Array> => {
    if (!cachedSecretStorageKey) {
        const { secretStorageKey } = await idpService.getMatrixSecretStorageKey();
        cachedSecretStorageKey = hexToBytes(secretStorageKey);
    }
    return cachedSecretStorageKey as Uint8Array;
};

const getSecretStorageKey: NonNullable<CryptoCallbacks['getSecretStorageKey']> = async ({ keys }) => {
    const keyId = Object.keys(keys)[0];
    if (!keyId) return null;
    return [keyId, await fetchSecretStorageKey()];
};

const cacheSecretStorageKey: NonNullable<CryptoCallbacks['cacheSecretStorageKey']> = (_keyId, _keyInfo, key) => {
    cachedSecretStorageKey = key;
};

// The widget must only flip to Connected once the first sync actually completes (PREPARED),
// not as soon as startClient() returns - startClient only kicks off the sync loop, it doesn't
// wait for a result, so resolving on it alone would report "connected" against a client whose
// room list/state is still empty.
const waitForPreparedSync = (client: MatrixClient): Promise<void> =>
    new Promise((resolve) => {
        if (client.getSyncState() === SyncState.Prepared) {
            resolve();
            return;
        }

        const onSync = (state: SyncState) => {
            if (state !== SyncState.Prepared) return;
            client.off(ClientEvent.Sync, onSync);
            resolve();
        };
        client.on(ClientEvent.Sync, onSync);
    });

export const createAndStartMatrixClient = async (session: MatrixSessionDto, forceNewDevice: boolean): Promise<MatrixClient> => {
    const client = sdk.createClient({
        baseUrl: process.env.REACT_APP_MATRIX_BASE_URL || '',
        userId: session.matrixUserId,
        accessToken: session.matrixAccessToken,
        deviceId: session.matrixDeviceId,
        cryptoCallbacks: { getSecretStorageKey, cacheSecretStorageKey }
    });

    // A device ID is only safe to reuse against the local crypto store it was created with -
    // the server permanently remembers the keys a device has published, so wiping the local
    // store on every load (while keeping the same device ID) causes key-upload conflicts, and
    // reusing a local store created for a different device ID causes the opposite crash. Clear
    // whenever this origin's last-used device ID doesn't match the one we've been given, or
    // whenever we asked the server to force a new device - in the latter case we can't assume
    // IndexedDB is actually empty (e.g. leftover state from an earlier, already-broken attempt),
    // so we always start clean rather than relying on the mismatch check to catch it.
    const lastDeviceId = localStorage.getItem(LAST_MATRIX_DEVICE_ID_KEY);
    const deviceChanged = lastDeviceId !== null && lastDeviceId !== session.matrixDeviceId;
    if (forceNewDevice || deviceChanged) {
        await client.clearStores();
    }
    localStorage.setItem(LAST_MATRIX_DEVICE_ID_KEY, session.matrixDeviceId);

    await client.initRustCrypto();
    await client.startClient({ initialSyncLimit: 20 });
    await waitForPreparedSync(client);

    // isCrossSigningReady()/getSessionBackupPrivateKey() can throw (e.g. matrix.ClientStoppedError
    // if the client is torn down mid-check, which happens routinely since Core remounts on every
    // in-app navigation) - wrapped so a failure here degrades to "skip bootstrap this connect,
    // retry next time" rather than failing the whole connect.
    //
    // Deliberately checking getSessionBackupPrivateKey() here, not getActiveSessionBackupVersion().
    // The Rust crypto engine runs its own automatic backup check on every startup, independent of
    // this app's bootstrap logic, and marks a backup "active" the moment it finds a trusted one on
    // the server - which is true for every device on an account except the very first, since by
    // then a backup already exists. That flag only means "this device is enabled to upload its own
    // future keys to that backup" - it says nothing about whether this device has ever obtained the
    // *decryption* key needed to read OLD sessions from it. Gating on it meant bootstrapEncryption
    // (and the loadSessionBackupPrivateKeyFromSecretStorage call inside it) never ran on any device
    // that wasn't the first, permanently skipping history recovery. getSessionBackupPrivateKey()
    // reports whether this device actually holds that decryption key, which is the real question.
    try {
        const cryptoApi = client.getCrypto();
        if (cryptoApi) {
            const [crossSigningReady, hasBackupKey] = await Promise.all([
                cryptoApi.isCrossSigningReady(),
                cryptoApi.getSessionBackupPrivateKey().then((key) => key !== null)
            ]);
            if (!crossSigningReady || !hasBackupKey) {
                bootstrapEncryption(client).catch((err) =>
                    console.error('bootstrapEncryption failed, continuing with baseline E2EE', err instanceof Error ? err.message : err)
                );
            }
        }
    } catch (err) {
        console.error('bootstrapEncryption readiness check failed, continuing with baseline E2EE', err instanceof Error ? err.message : err);
    }

    return client;
};

export const getOrCreateMatrixClient = async (
    fetchSession: (clientInstanceId: string, forceNewDevice: boolean) => Promise<MatrixSessionDto>
): Promise<MatrixClient> => {
    if (cachedClient) return cachedClient;

    if (!cachedClientPromise) {
        cachedClientPromise = (async () => {
            const clientInstanceId = getOrCreateClientInstanceId();
            // No locally recorded device for this origin means we can't trust that any cached
            // session the server hands back actually matches a crypto identity we hold locally
            // (e.g. this origin never connected before, or storage was cleared) - ask the server
            // to rotate this origin's device rather than risk reusing one registered elsewhere.
            const forceNewDevice = localStorage.getItem(LAST_MATRIX_DEVICE_ID_KEY) === null;
            const session = await fetchSession(clientInstanceId, forceNewDevice);
            const client = await createAndStartMatrixClient(session, forceNewDevice);
            cachedClient = client;
            return client;
        })().catch((error) => {
            cachedClientPromise = null;
            throw error;
        });
    }

    return cachedClientPromise;
};

export const stopMatrixClient = (client: MatrixClient): void => {
    client.stopClient();

    if (cachedClient === client) {
        cachedClient = null;
        cachedClientPromise = null;
        cachedSecretStorageKey = null;
    }
};

// Tears down both the backend session (so the homeserver revokes the access token) and the
// local client. The local stop still runs even if the backend call fails, so a network error
// on disconnect can never leave the widget stuck pointing at a client that no longer receives
// events while still claiming to be connected.
export const disconnectMatrixSession = async (client: MatrixClient, destroySession: () => Promise<void>): Promise<void> => {
    try {
        await destroySession();
    } finally {
        stopMatrixClient(client);
    }
};

// getKeyBackupInfo() cannot distinguish "confirmed no backup" from "the check itself failed" -
// verified against matrix-js-sdk's RustBackupManager.doCheckKeyBackup, which swallows any
// network/server error and returns null either way. Since creating a new backup
// (bootstrapSecretStorage's setupNewKeyBackup -> resetKeyBackup) deletes every existing backup
// version on the server first, trusting a single null result risks permanently destroying a real
// backup on a transient blip. Require two independent null results, spaced apart, before treating
// this as confirmed-absent rather than merely unlucky.
const confirmNoExistingBackup = async (cryptoApi: CryptoApi): Promise<boolean> => {
    const first = await cryptoApi.getKeyBackupInfo();
    if (first !== null) return false;

    await new Promise((resolve) => setTimeout(resolve, 2000));

    const second = await cryptoApi.getKeyBackupInfo();
    return second === null;
};

// Bootstraps this device's encryption trust and history-recovery state: secret storage (SSSS),
// cross-signing, and key backup. The secret-storage key is backend-custodial (the server holds it
// on the user's behalf, decrypted only via GET /v1/me/matrix-session/secret-storage-key) rather
// than a user-entered recovery phrase - every device, including one that's never connected before,
// can silently fetch the same key and reach the same result with no user-visible prompt. Called
// fire-and-forget from createAndStartMatrixClient - a failure here (e.g. the backend endpoint isn't
// live yet) must never block connect; baseline E2EE (already active via initRustCrypto) keeps
// working regardless, just without cross-device history recovery until this succeeds.
export const bootstrapEncryption = async (client: MatrixClient): Promise<void> => {
    const cryptoApi = client.getCrypto();
    if (!cryptoApi) throw new Error('bootstrapEncryption called before initRustCrypto');

    // Cross-signing bootstrapped before secret storage (rather than after) so that when
    // bootstrapSecretStorage runs next, the cross-signing private keys it already knows about get
    // stored into secret storage as part of its own normal logic - not via CrossSigningIdentity's
    // internal fallback path, which duplicates that write less predictably.
    if (!(await cryptoApi.isCrossSigningReady())) {
        const authUploadDeviceSigningKeys: UIAuthCallback<void> = async (makeRequest) => {
            const { password, matrixUserId } = await idpService.getMatrixPassword();
            await makeRequest({
                type: 'm.login.password',
                identifier: { type: 'm.id.user', user: matrixUserId },
                password
            });
        };
        await cryptoApi.bootstrapCrossSigning({ authUploadDeviceSigningKeys });
    }

    const createSecretStorageKey = async (): Promise<GeneratedSecretStorageKey> => ({
        privateKey: await fetchSecretStorageKey()
    });

    // Only the first device to ever bootstrap this account should mint a new backup version -
    // every later device must attach to that same one, or backups fragment across devices and
    // none of them ends up with the full history.
    const noExistingBackup = await confirmNoExistingBackup(cryptoApi);
    await cryptoApi.bootstrapSecretStorage({
        createSecretStorageKey,
        setupNewKeyBackup: noExistingBackup
    });

    // A device attaching to a backup it didn't just create must explicitly load the shared,
    // server-custodied decryption key and pull every session it's missing - this is what actually
    // makes pre-existing history readable on a brand-new origin. Checking
    // getSessionBackupPrivateKey() rather than getActiveSessionBackupVersion() here for the same
    // reason as the caller's readiness check above: the latter goes true as soon as the Rust
    // crypto engine's own automatic check finds a trusted backup on the server, regardless of
    // whether this device has actually loaded the decryption key - which is what this block exists
    // to do, so it must not skip itself based on that flag.
    if (!noExistingBackup && (await cryptoApi.getSessionBackupPrivateKey()) === null) {
        try {
            await cryptoApi.loadSessionBackupPrivateKeyFromSecretStorage();
            await cryptoApi.checkKeyBackupAndEnable();
            await cryptoApi.restoreKeyBackup();
        } catch (err) {
            // Deliberately do NOT call resetKeyBackup() here: that deletes the account's real
            // backup on the server and replaces it with an empty one, which used to run on every
            // failure here - including transient ones (network blips, a cold device's first
            // attempt) - permanently destroying history recovery for every device, past and
            // future, on a single bad attempt. Leaving getActiveSessionBackupVersion() null
            // instead means this block simply runs again next connect (see the
            // !crossSigningReady || !backupActive gate above), so a real transient failure
            // self-heals and a genuinely unrecoverable backup just keeps failing harmlessly
            // instead of taking the working backup down with it.
            console.error('Failed to restore existing key backup, will retry on next connect', err instanceof Error ? err.message : err);
        }
    }
};
